<script setup lang="ts">
import { X } from 'lucide-vue-next'
import type { PanelTab } from '../../../lib/brandAppSections'
import type { BrandAppShowcaseStore } from '../../../lib/brandAppContract'
import PanelGroup from '../../PanelGroup.vue'
import ImageField from '../../ImageField.vue'
import BlockTitleField from './BlockTitleField.vue'
import useBrandAppConfig from '../../../composables/useBrandAppConfig'
import { isInvalidUrlInput, safeHttpUrl } from '../../../lib/brandAppUrl'

const props = defineProps<{ tab?: PanelTab; stores: BrandAppShowcaseStore[] }>()

const { t } = useI18n()
const { home, updateHome, order, updateOrder, isStoreHidden } = useBrandAppConfig()

const K = 'pages.loyaltySupports.webapp.editor.menu'

const visibleStores = computed(() => props.stores.filter((store) => !isStoreHidden(store.id)))

const hasSeveralStores = computed(() => visibleStores.value.length > 1)

const exceptions = computed(() => visibleStores.value.filter((store) => store.id in home.value.menuStoreUrls))

const availableStores = computed(() => visibleStores.value.filter((store) => !(store.id in home.value.menuStoreUrls)))

const isEveryStoreOverridden = computed(() => hasSeveralStores.value && availableStores.value.length === 0)

const coveredCount = computed(() =>
  safeHttpUrl(order.value.catalogUrl)
    ? visibleStores.value.length
    : exceptions.value.filter((store) => safeHttpUrl(home.value.menuStoreUrls[store.id])).length
)

const setStoreUrl = (storeId: number, url: string) => {
  updateHome('menuStoreUrls', { ...home.value.menuStoreUrls, [storeId]: url })
}

const addException = (value: unknown) => {
  const storeId = Number(value)
  if (!Number.isInteger(storeId) || storeId in home.value.menuStoreUrls) return
  setStoreUrl(storeId, '')
}

const removeException = (storeId: number) => {
  const next = { ...home.value.menuStoreUrls }
  delete next[storeId]
  updateHome('menuStoreUrls', next)
}

const linkErrors = (value: string | undefined) =>
  isInvalidUrlInput(value) ? [t('pages.loyaltySupports.webapp.editor.validation.invalidUrl')] : []

const storeLinkErrors = (value: string) => (value.trim() === '' ? [t(`${K}.storeLinkRequired`)] : linkErrors(value))
</script>

<template>
  <PanelGroup v-if="tab !== 'content'">
    <BlockTitleField block-id="menu" />
  </PanelGroup>

  <PanelGroup v-if="tab !== 'text'">
    <div class="flex flex-col gap-5">
      <div class="flex flex-col gap-2">
        <Label for="brandApp.home.menuImage">{{ t(`${K}.image`) }}</Label>
        <ImageField
          id="brandApp.home.menuImage"
          :model-value="home.menuImage"
          :help="t(`${K}.imageHelp`)"
          @update:model-value="updateHome('menuImage', $event)"
        />
      </div>

      <div class="flex flex-col gap-2">
        <AppFormInput
          id="brandApp.order.catalogUrl"
          :label="hasSeveralStores ? t(`${K}.sharedLinkLabel`) : t(`${K}.catalogUrlLabel`)"
          placeholder="https://www.mon-restaurant.fr/carte.pdf"
          :model-value="order.catalogUrl"
          :disabled="isEveryStoreOverridden"
          :errors="linkErrors(order.catalogUrl)"
          @update:model-value="updateOrder('catalogUrl', String($event))"
        />
        <p v-if="hasSeveralStores" class="text-muted-foreground text-xs">
          {{
            isEveryStoreOverridden
              ? t(`${K}.everyStoreOverridden`)
              : t(`${K}.storesCovered`, { count: coveredCount, total: visibleStores.length })
          }}
        </p>
      </div>

      <div v-if="hasSeveralStores" class="flex flex-col gap-3">
        <Label>{{ t(`${K}.exceptions`) }}</Label>

        <div v-for="store in exceptions" :key="store.id" class="flex items-end gap-2">
          <AppFormInput
            :id="`brandApp.home.menuStoreUrls.${store.id}`"
            class="min-w-0 flex-1"
            :label="store.name"
            placeholder="https://www.mon-restaurant.fr/carte-boutique.pdf"
            :model-value="home.menuStoreUrls[store.id]"
            :errors="storeLinkErrors(home.menuStoreUrls[store.id])"
            @update:model-value="setStoreUrl(store.id, String($event))"
          />
          <Button
            variant="ghost"
            size="icon"
            class="text-muted-foreground shrink-0"
            :aria-label="t(`${K}.removeStoreLink`, { name: store.name })"
            @click="removeException(store.id)"
          >
            <X class="size-4" />
          </Button>
        </div>

        <Select v-if="availableStores.length" model-value="" @update:model-value="addException">
          <SelectTrigger class="w-full" :aria-label="t(`${K}.addStoreLink`)">
            <SelectValue :placeholder="t(`${K}.addStoreLink`)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="store in availableStores" :key="store.id" :value="String(store.id)">
              {{ store.name }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  </PanelGroup>
</template>
